<template>
  <component
    :is="isKitsPage ? ElevatedCard : 'div'"
    :compact="isKitsPage || undefined"
    class="flex flex-col gap-3"
    :class="isKitsPage ? '-order-2' : undefined"
  >
    <!-- Kit price header + out-of-stock status: travels with the notify form as
    one reordered unit, so it always renders above it, inside the same card
    boundary as the notify form (matches the reference design's "Product Info
    Card" grouping). -->
    <template v-if="isKitsPage">
      <div class="flex items-baseline gap-2">
        <span class="text-h5 text-gray-900 font-semibold">
          {{ formattedPrice }}
        </span>
        <span class="text-sm text-gray-500">
          {{ t('product.show.checkout.perBoxLabel') }}
        </span>
        <span class="text-gray-400 text-xs">
          {{ t('product.show.checkout.shipping.excludingShipping') }}
        </span>
      </div>
      <hr class="-mx-4" />
      <div
        class="flex items-center justify-center gap-2 text-sm font-medium text-stone-700"
      >
        <Check class="w-4 h-4 text-teal-600 shrink-0" />
        {{ t('product.show.noInventory.title') }}
      </div>
    </template>

    <div
      class="flex flex-col gap-2"
      :class="
        isKitsPage
          ? 'bg-teal-50 border border-teal-200 rounded-2xl p-3'
          : undefined
      "
    >
      <template v-if="!subscribed">
        <div
          class="flex items-center justify-center gap-2 text-sm font-medium text-gray-700"
        >
          <Bell class="w-4 h-4 text-teal-600" />
          {{ t('product.show.noDates.notify.title') }}
        </div>
        <div
          class="flex items-center border rounded-2xl p-1 pr-2 py-2 transition-colors focus-within:border-teal-600"
          :class="[
            emailError ? 'border-red-400' : 'border-gray-200',
            isKitsPage ? 'bg-white' : undefined,
          ]"
        >
          <input
            v-model="email"
            type="email"
            :placeholder="t('product.show.noDates.notify.placeholder')"
            class="flex-1 min-w-0 px-3 py-1.5 text-sm outline-hidden bg-transparent"
            @input="emailError = false"
            @keydown.enter="subscribe"
          />
          <Button
            data-test-id="buy-box-no-dates-notify-button"
            variant="contained"
            class="py-1.5 px-4 shrink-0 rounded-xl text-sm"
            @click="subscribe"
          >
            {{ t('product.show.noDates.notify.cta') }}
          </Button>
        </div>
        <p
          v-if="emailError"
          class="text-red-500 text-xs flex items-center gap-1"
        >
          <InfoCircleIcon class="w-3.5 h-3.5 shrink-0" />
          {{ t('product.show.noDates.notify.emailError') }}
        </p>
      </template>

      <!-- On kits pages the surrounding block already provides the teal
      treatment, so the success state stays unstyled there. -->
      <div
        v-else
        class="flex flex-col items-center gap-2 text-center"
        :class="
          isKitsPage
            ? undefined
            : 'bg-teal-50 border border-teal-200 rounded-2xl px-4 py-5'
        "
      >
        <span class="flex items-center gap-2">
          <CheckCircle class="w-5 h-5 text-teal-600 shrink-0" />
          <p class="font-semibold text-sm text-stone-700">
            {{ t('product.show.noDates.notify.successTitle') }}
          </p>
        </span>
        <p class="text-sm text-gray-500">
          {{ t('product.show.noDates.notify.successBody') }}
        </p>
      </div>
    </div>

    <p v-if="!isKitsPage" class="text-xs text-center text-gray-400">
      {{ t('product.show.noDates.notify.subtext') }}
    </p>
  </component>
</template>

<script setup lang="ts">
import { Bell, Check, CheckCircle } from 'lucide-vue-next'
import { ElevatedCard } from '#components'
import type { EventDescription } from '~/composables/types/event'
import InfoCircleIcon from '~/assets/icons/info-circle.svg?component'

defineProps<{
  /** Renders the kit price header, out-of-stock status and card treatment. */
  isKitsPage?: boolean
  /** Already-formatted price, only rendered on kits pages. */
  formattedPrice?: string
}>()

const { t } = useI18n()
const eventData = inject<EventDescription>('eventData')!

const email = ref('')
const emailError = ref(false)
const subscribed = ref(false)

const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)

const subscribe = async () => {
  if (!isValidEmail(email.value)) {
    emailError.value = true
    return
  }
  try {
    const response = await kftApiRequest(
      `v1/store/events/${eventGetters.getId(eventData)}/subscribe`,
      { body: { email: email.value } }
    )
    subscribed.value = true
    sendWaitlistForm(response?.data?.sha256_email_address)
  } catch {
    emailError.value = true
  }
}
</script>
